/* eslint-disable array-callback-return */
/* eslint-disable max-len */
import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";

import {
  Input,
  AutoSaveField as Field,
  AsyncSelectAutoComplete,
  SelectAutocomplete,
  InlineRadioGroup
} from "../../../../../../components/Form";
import {
  getInvalidMessage,
  getNotTrimmedMessage
} from "../../../../../../utils/getValidationMessage";
import { ButtonWrapper, RemoveButton, AddButton, FormContainer } from "./styled";
import {
  ALPHANUMERIC_REGEX,
  NUMBER_REGEX,
  SPECIAL_CHARACTERS_REGEX,
  UAE_IBAN_REGEX,
  DECIMAL_TWO_DIGIT_DECIMAL_REGEX,
  checkIsTrimmed
} from "../../../../../../utils/validation";
import { yesNoOptions, YesNoList } from "../../../../../../constants/options";
import { baseLocation } from "./constants";
import { SignatoryUAE } from "./SignatoryUAE";
import { SignatoryNonUAE } from "./SignatoryNonUAE";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { ArrayAddButton } from "../../../Buttons/ArrayAddButton";
import { ArrayRemoveButton } from "../../../Buttons/ArrayRemoveButton";
import { getObjectUsingDatalistValue } from "../../../../../../utils/helper";
import { getUAEBankName } from "../../../../../../store/selectors/appConfig";
import { ScrollToError } from "components/ScrollToError";

const SignatoryBankingRelationshipsSchema = () =>
  Yup.object().shape({
    havePersonalBorrowings: Yup.string()
      .nullable()
      .max(10, "Maximum 10 characters allowed"),
    otherBankLoanExist: Yup.string()
      .nullable()
      .max(10, "Maximum 10 characters allowed"),
    otherBankLoanRelationship: Yup.array().of(
      Yup.object().shape({
        basedIn: Yup.string()
          .nullable()
          .max(50, "Maximum 50 characters allowed"),
        loanAvailedDate: Yup.date()
          .nullable()
          .max(new Date(), getInvalidMessage("Date Loan facility availed"))
          .typeError(getInvalidMessage("Date Loan facility availed")),
        emiPaid: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(NUMBER_REGEX, getInvalidMessage("EMI paid/ tenure paid")),
        emiAmount: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(DECIMAL_TWO_DIGIT_DECIMAL_REGEX, getInvalidMessage("EMI amount")),
        emiClearing: Yup.date()
          .nullable()
          .typeError(getInvalidMessage("EMI Clearing")),
        clearingBank: Yup.string()
          .nullable()
          .max(50, "Maximum 50 characters allowed"),
        takeOverBank: Yup.string()
          .nullable()
          .max(50, "Maximum 50 characters allowed"),
        outstandingExposure: Yup.string()
          .max(13, "Maximum 13 characters allowed")
          .matches(NUMBER_REGEX, getInvalidMessage("Outstanding or Clean Exposure")),
        facilityLetterProvided: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed"),
        howPreviousRBLUtilised: Yup.string()
          .nullable()
          .max(255, "Maximum 255 characters allowed")
          .matches(
            SPECIAL_CHARACTERS_REGEX,
            getInvalidMessage("Comment on how was the previous RBL utilised")
          ),
        howOtherBankLoanUtilised: Yup.string()
          .nullable()
          .max(255, "Maximum 255 characters allowed")
          .matches(
            SPECIAL_CHARACTERS_REGEX,
            getInvalidMessage(
              "Comment on how was Other Bank business loans utilised since previous RBL or 12 months( whichever was most recent)"
            )
          ),
        iban: Yup.string()
          .nullable()
          .when("basedIn", {
            is: basedIn => basedIn === "Within UAE",
            then: Yup.string() // NOSONAR
              .nullable()
              .matches(UAE_IBAN_REGEX, getInvalidMessage("IBAN"))
              .max(23, "Maximum 23 characters allowed"),
            otherwise: Yup.string()
              .nullable()
              .matches(ALPHANUMERIC_REGEX, getInvalidMessage("IBAN"))
              .max(23, "Maximum 23 characters allowed")
          }),
        loanAmount: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(NUMBER_REGEX, getInvalidMessage("Finance/Facility Amount")),
        currency: Yup.string()
          .nullable()
          .max(3, "Maximum 3 characters allowed"),
        bankName: Yup.string().when("basedIn", {
          is: basedIn => basedIn !== "Within UAE",
          then: Yup.string() // NOSONAR
            .nullable()
            .max(50, "Maximum 50 characters allowed")
            .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Bank name"))
            .test("space validation", getNotTrimmedMessage("Bank name"), checkIsTrimmed),
          otherwise: Yup.string().nullable()
        }),
        loanFacility: Yup.string()
          .nullable()
          .max(50, "Maximum 50 characters allowed")
      })
    )
  });

const initialValues = {
  havePersonalBorrowings: "",
  otherBankLoanExist: "",
  otherBankLoanRelationship: [
    {
      basedIn: "",
      bankName: "",
      iban: "",
      currency: "",
      loanFacility: "",
      loanAvailedDate: "",
      emiPaid: "",
      emiAmount: "",
      clearingBank: "",
      takeOverBank: "",
      outstandingExposure: "",
      facilityLetterProvided: "",
      howPreviousRBLUtilised: "",
      howOtherBankLoanUtilised: "",
      loanAmount: ""
    }
  ]
};
export const SignatoryBankingRelationshipsComponent = ({
  handleContinue,
  stakeholder,
  index,
  sIndex = 0,
  updateRoProspect,
  getDatalistSearch
}) => {
  const dataList = useSelector(getUAEBankName);
  const ANOTHER_BANK_COUNT = 5;
  const basePath = `roProspect.signatoryInfo[${sIndex}]`;
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SignatoryBankingRelationshipsSchema}
      onSubmit={handleContinue}
      validateOnChange={false}
    >
      {({ values }) => {
        const showRemoveBtn = values.otherBankLoanRelationship.length > 1;
        let bankSelected = {};
        let bankSelectedArr = [];
        let uaeClearingBankSelected = {};
        let uaeTakeOverBankSelected = {};
        let uaeTakeOverBankArr = [];
        let uaeClearingBankArr = [];
        values.otherBankLoanRelationship.forEach((_items, bankIndex) => {
          if (dataList) {
            if (values.otherBankLoanRelationship[bankIndex].bankName !== "") {
              bankSelected = getObjectUsingDatalistValue(
                dataList,
                values.otherBankLoanRelationship[bankIndex].bankName
              );
              bankSelectedArr.push(bankSelected);
            }
            if (values.otherBankLoanRelationship[bankIndex].clearingBank !== "") {
              uaeClearingBankSelected = getObjectUsingDatalistValue(
                dataList,
                values.otherBankLoanRelationship[bankIndex].clearingBank
              );
              uaeClearingBankArr.push(uaeClearingBankSelected);
            }
            if (values.otherBankLoanRelationship[bankIndex].takeOverBank !== "") {
              uaeTakeOverBankSelected = getObjectUsingDatalistValue(
                dataList,
                values.otherBankLoanRelationship[bankIndex].takeOverBank
              );
              uaeTakeOverBankArr.push(uaeTakeOverBankSelected);
            }
          }
        });
        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <Field
                  name="havePersonalBorrowings"
                  path={`${basePath}.havePersonalBorrowings`}
                  label="Does Signatory have personal borrowings"
                  placeholder="Does Signatory have personal borrowings"
                  component={InlineRadioGroup}
                  options={YesNoList}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="otherBankLoanExist"
                  path={`${basePath}.otherBankLoanExist`}
                  label="Borrowing/Finance with any other Banks?"
                  placeholder="Borrowing/Finance with any other Banks?"
                  component={InlineRadioGroup}
                  options={yesNoOptions}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              {values.otherBankLoanExist && (
                <FieldArray name="otherBankLoanRelationship">
                  {arrayHelpers => (
                    <>
                      {values.otherBankLoanRelationship.map((bankingDetail, bankIndex) => (
                        <React.Fragment key={bankIndex}>
                          <Grid item sm={12} xs={12}>
                            <Field
                              name={`otherBankLoanRelationship[${bankIndex}].basedIn`}
                              path={`${basePath}.otherBankLoanRelationship[${bankIndex}].basedIn`}
                              label="Where are they based?"
                              placeholder="Where are they based?"
                              component={InlineRadioGroup}
                              options={baseLocation}
                              InputProps={{
                                inputProps: { tabIndex: 0 }
                              }}
                            />
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            {bankingDetail.basedIn === "Within UAE" ? (
                              <Field
                                name={`otherBankLoanRelationship[${bankIndex}].bankName`}
                                path={`${basePath}.otherBankLoanRelationship[${bankIndex}].bankName`}
                                label="Bank name"
                                component={AsyncSelectAutoComplete}
                                codeType="UAEB"
                                defaultValue={bankSelectedArr[bankIndex]}
                                getDatalistSearch={getDatalistSearch}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                              />
                            ) : (
                              <Field
                                name={`otherBankLoanRelationship[${bankIndex}].bankName`}
                                path={`${basePath}.otherBankLoanRelationship[${bankIndex}].bankName`}
                                label="Name of the Lender/ Bank Name"
                                placeholder="Name of the Lender/ Bank Name"
                                component={Input}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                              />
                            )}
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <Field
                              name={`otherBankLoanRelationship[${bankIndex}].iban`}
                              path={`${basePath}.otherBankLoanRelationship[${bankIndex}].iban`}
                              label="IBAN"
                              placeholder="IBAN"
                              component={Input}
                              InputProps={{
                                inputProps: { tabIndex: 0, maxLength: 23 }
                              }}
                            />
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <Field
                              name={`otherBankLoanRelationship[${bankIndex}].currency`}
                              path={`${basePath}.otherBankLoanRelationship[${bankIndex}].currency`}
                              label="Currency"
                              placeholder="Currency"
                              InputProps={{
                                inputProps: { tabIndex: 0 }
                              }}
                              component={SelectAutocomplete}
                              datalistId="accountCurrencies"
                            />
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <Field
                              name={`otherBankLoanRelationship[${bankIndex}].loanFacility`}
                              path={`${basePath}.otherBankLoanRelationship[${bankIndex}].loanFacility`}
                              label={
                                bankingDetail.basedIn === "Within UAE"
                                  ? "Loan facility"
                                  : "Finance/Facility Type"
                              }
                              placeholder={
                                bankingDetail.basedIn === "Within UAE"
                                  ? "Loan facility"
                                  : "Finance/Facility Type"
                              }
                              component={Input}
                              InputProps={{
                                inputProps: { tabIndex: 0 }
                              }}
                            />
                          </Grid>
                          {bankingDetail.basedIn === "Within UAE" ? (
                            <SignatoryUAE
                              uaeClearingBankArr={uaeClearingBankArr}
                              uaeTakeOverBankArr={uaeTakeOverBankArr}
                              getDatalistSearch={getDatalistSearch}
                              basePath={basePath}
                              bankIndex={bankIndex}
                            />
                          ) : bankingDetail.basedIn === "Outside UAE" ? (
                            <SignatoryNonUAE basePath={basePath} bankIndex={bankIndex} />
                          ) : (
                            ""
                          )}
                          <>
                            {showRemoveBtn && (
                              <Grid item sm={12} xs={12}>
                                <ArrayRemoveButton
                                  arrayHelpers={arrayHelpers}
                                  dataArray={values.otherBankLoanRelationship}
                                  itemIndex={index}
                                  updateProspect={updateRoProspect}
                                  title="Delete"
                                  className={RemoveButton}
                                />
                              </Grid>
                            )}
                          </>
                        </React.Fragment>
                      ))}
                      <>
                        {values.otherBankLoanRelationship.length < ANOTHER_BANK_COUNT && (
                          <Grid item sm={12} xs={12}>
                            <ArrayAddButton
                              title="Add another bank"
                              limit={ANOTHER_BANK_COUNT}
                              requiredFields={["bankName", "iban"]}
                              addedItem={{ bankName: "", iban: "" }}
                              dataArray={values.otherBankLoanRelationship}
                              arrayHelpers={arrayHelpers}
                              className={AddButton}
                            />
                          </Grid>
                        )}
                      </>
                    </>
                  )}
                </FieldArray>
              )}
              <ButtonWrapper>
                <ContinueButton type="submit" />
              </ButtonWrapper>
            </Grid>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

SignatoryBankingRelationshipsComponent.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Stakeholder information object
   */
  stakeholder: PropTypes.shape({
    signatoryId: PropTypes.string,
    fullName: PropTypes.string,
    isShareholderACompany: PropTypes.bool,
    isSignatory: PropTypes.bool
  }),

  /**
   * Index of the component in the list
   */
  index: PropTypes.number,

  /**
   * Index of the signatory in the signatories array
   */
  sIndex: PropTypes.number,

  /**
   * Function to update RO prospect data in Redux store
   */
  updateRoProspect: PropTypes.func.isRequired,

  /**
   * Function to search datalist by code type
   */
  getDatalistSearch: PropTypes.func.isRequired
};
